import React, { createContext, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  ADD_MESSAGE,
  UPDATE_MESSAGE,
  ADD_THREAD,
  ADD_HANDOFF,
  UNREAD_COUNT,
  GET_MESSAGES,
  GET_ACTION_LOGS
} from 'src/actions/chatActions';
import { LAST_SEEN } from 'src/actions/leadActions';
import { addNotifications } from './actions/notificationsActions';
import NotificationSound from 'src/assets/sounds/facebookchat.mp3';
import _ from 'lodash';
import {
  initiateSocket,
  disconnectSocket,
  subscribeToChat
} from './services/socket';

const WebSocketContext = createContext(null);

export { WebSocketContext };

export default ({ children, room }) => {
  const [socket, setSocket] = useState(null);
  const dispatch = useDispatch();
  let audio = new Audio(NotificationSound);

  const createNotification = _.debounce(async msg => {
    if (document.visibilityState === 'hidden') {
      dispatch(addNotifications());
      await audio.play().catch(err => {});
      document.title = msg;
    }
  }, 1000);

  const connectSocket = useCallback(() => {
    if (room) {
      const newSocket = initiateSocket(room);
      setSocket(newSocket);
      return newSocket;
    }
    return null;
  }, [room]);

  const reconnectSocket = useCallback(() => {
    disconnectSocket();
    const newSocket = connectSocket();
    if (newSocket) {
      setupSocketListeners(newSocket);
    }
  }, [connectSocket]);

  const setupSocketListeners = useCallback((ws) => {
    subscribeToChat(async (err, data) => {
      if (err) return;
      switch (data.type) {
        case 'actionLogs':
          dispatch({
            type: GET_ACTION_LOGS,
            payload: { logs: data.logs }
          });
          break;
        case 'newMessage':
          dispatch({
            type: ADD_MESSAGE,
            payload: { message: data.message }
          });

          await createNotification('New Messages');
          break;
        case 'updateMessage':
          dispatch({
            type: UPDATE_MESSAGE,
            payload: { message: data.message }
          });
          break;
        case 'unreadCount':
          dispatch({
            type: UNREAD_COUNT,
            payload: data.unreadCount
          });
          dispatch({
            type: LAST_SEEN,
            payload: data.unreadCount
          });
          break;
        case 'newThread':
          dispatch({
            type: ADD_THREAD,
            payload: { thread: data.thread }
          });

          await createNotification('New Conversation!');
          break;
        case 'newHandoff':
          dispatch({
            type: ADD_HANDOFF,
            payload: { handoff: data.handoff }
          });
          await createNotification('New Ticket!');
          break;
        case 'updateHandoff':
          dispatch({
            type: ADD_HANDOFF,
            payload: { handoff: data.handoff }
          });
          break;
        case 'messageHistory':
          dispatch({
            type: GET_MESSAGES,
            payload: {
              threadId: data.id,
              messages: data.messages
            }
          });
          dispatch({
            type: ADD_THREAD,
            payload: { thread: _.omit(data, ['messages']) }
          });
          break;

        default:
          console.log('usupported event: ', data.type);
          break;
      }
    });
  }, [dispatch]);

  useEffect(() => {
    const ws = connectSocket();
    if (ws) {
      setupSocketListeners(ws);
    }

    return () => {
      disconnectSocket();
    };
  }, [room, connectSocket, setupSocketListeners]);

  const contextValue = {
    socket,
    reconnectSocket
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};